import { ACTION_TYPES, CONTENT_TYPES, savedContentTypeMapping } from '@/util/access_visibility'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { getContentCountsForUser } from './api/dynamodb'
import { getUserDownloads, saveUserDownload } from './api/downloads'
import { defineStore } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useLibraryStore } from '@/library/library.store'

interface ContentAccessReport {
  toolkit_resource_download_count: number
  toolkit_resource_download_quota: number
  toolkit_copy_count: number
  toolkit_copy_quota: number
  toolkit_print_count: number
  toolkit_print_quota: number
  error: string | null
  response: string | null
  content_access_report_error: string | null
}

interface ContentAccessStatus {
  copyQuotaExceeded: boolean,
  downloadQuotaExceeded: boolean,
  printQuotaExceeded: boolean,
  redownloadMap: { [key: string]: boolean },
  report: ContentAccessReport,
  reportError: string | null
}

export const useContentAccessStore = defineStore('contentAccess', {
  state: () => ({
    content_access_report_error: null as string | null,
    content_access_report: {
      toolkit_resource_download_count: 0,
      toolkit_resource_download_quota: 0,
      toolkit_copy_count: 0,
      toolkit_copy_quota: 0,
      toolkit_print_count: 0,
      toolkit_print_quota: 0
    } as ContentAccessReport,
    downloaded_uuids: [] as string[],

    // hardcoded defaults which should be replaced when we load user info during initialization
    libraryQuotas: {
      maxFolders: 50,
      maxGoalsPerFolder: 15,
      maxStrategiesPerFolder: 15,
      maxPresentlevelsPerFolder: 5,
      maxResourcesPerFolder: 20,
      shouldEnforce: true
    },
    refetchTimeout: null as ReturnType<typeof setTimeout> | null
  }),
  actions: {
    setContentAccessReport(report: ContentAccessReport) {
      if (!report) return
      if (report.error) {
        this.content_access_report_error = report.response || 'error fetching content access report'
      } else {
        this.content_access_report_error = null
      }
      this.content_access_report.toolkit_copy_count = report.toolkit_copy_count || 0
      this.content_access_report.toolkit_copy_quota = report.toolkit_copy_quota || 0
      this.content_access_report.toolkit_print_count = report.toolkit_print_count || 0
      this.content_access_report.toolkit_print_quota = report.toolkit_print_quota || 0
      this.content_access_report.toolkit_resource_download_count =
        report.toolkit_resource_download_count || 0
      this.content_access_report.toolkit_resource_download_quota =
        report.toolkit_resource_download_quota || 0
    },
    incrementCopyCount() {
      if (this.content_access_report.toolkit_copy_count) {
        this.content_access_report.toolkit_copy_count += 1
      } else {
        this.content_access_report.toolkit_copy_count = 1
      }
    },
    incrementDownloadCount() {
      if (this.content_access_report.toolkit_resource_download_count) {
        this.content_access_report.toolkit_resource_download_count += 1
      }
      else {
        this.content_access_report.toolkit_resource_download_count = 1
      }
    },
    incrementPrintCount() {
      if (this.content_access_report.toolkit_print_count) {
        this.content_access_report.toolkit_print_count += 1
      } else {
        this.content_access_report.toolkit_print_count = 1
      }
    },
    setDownloadedUuids(list: string[]) {
      this.downloaded_uuids = list || []
    },
    addDownloadedUuid(uuid: string) {
      this.downloaded_uuids.push(uuid)
    },
    setLibraryQuotasFromTkAccount(tkAccount: { max_students: number, max_goals_per_student: number, max_strategies_per_student: number, max_resources_per_student: number, max_presentlevels_per_student: number, enforce_library_quotas: boolean }) {
      if (!tkAccount) return
      this.libraryQuotas.maxFolders = tkAccount.max_students
      this.libraryQuotas.maxGoalsPerFolder = tkAccount.max_goals_per_student
      this.libraryQuotas.maxStrategiesPerFolder = tkAccount.max_strategies_per_student
      this.libraryQuotas.maxResourcesPerFolder = tkAccount.max_resources_per_student
      this.libraryQuotas.maxPresentlevelsPerFolder = tkAccount.max_presentlevels_per_student
      this.libraryQuotas.shouldEnforce = tkAccount.enforce_library_quotas
    },
    trackPrint({ contentType, contentTitle, contentUuid, otherData }: { contentType: string, contentTitle: string, contentUuid: string, otherData: object }) {
      const data = Object.assign(otherData, {
        content_type: contentType,
        content_title: contentTitle,
        content_uuid: contentUuid,
        print_count: this.printCount,
        content_access_report_error: null as null | string,
      })
      if (this.contentAccessStatus.reportError) {
        data.content_access_report_error = this.contentAccessStatus.reportError
      }
      const contentInfo = {
        cu: contentUuid,
        ct: contentTitle,
        ctype: contentType
      }
      const userStore = useUserStore()
      const analytics = getAnalyticsTracker()
      analytics.sendEvent('Printed Content', {
        ...data,
        ...contentInfo,
        ...userStore.eventMetadata
      })
      this.incrementPrintCount()
      this.refetchContentAccessReport()
    },
    async trackDownload({ resource }: { resource: { uuid: string } }) {
      const isRedownload = this.redownloadMap[resource.uuid] || false
      if (!isRedownload) {
        this.incrementDownloadCount()
        this.addDownloadedUuid(resource.uuid)
      }

      // persist to download history collection
      const userStore = useUserStore()
      if (userStore.isAuthenticated) {
        await saveUserDownload({ content_type: 'resource', content_uuid: resource.uuid })
      }
      this.refetchContentAccessReport()
    },
    sendContentAccessBlockedQuota({ actionType, contentInfo }: { actionType: string, contentInfo: { ct: string, cu: string, ctype: string } }) {
      const data = {} as {
        title: string | null,
        resource_uuid: string,
        blocked_action: string,
        blocking_quota: string,
        blocking_count: number,
        blocking_quota_value: number,
        content_type: string | null,
        content_access_report_error: string | null,
      }
      switch (actionType) {
        case ACTION_TYPES.DOWNLOAD:
          data.title = contentInfo.ct
          data.resource_uuid = contentInfo.cu
          data.blocked_action = 'Download Resource'
          data.blocking_quota = 'toolkit_resource_download_quota'
          data.blocking_count = this.downloadCount
          data.blocking_quota_value = this.downloadQuota
          break
        case ACTION_TYPES.COPY:
          data.blocked_action = 'Copy Text'
          data.blocking_quota = 'toolkit_copy_quota'
          data.blocking_count = this.copyCount
          data.blocking_quota_value = this.copyQuota
          break
        case ACTION_TYPES.PRINT:
          data.blocked_action = 'Print Content'
          data.blocking_quota = 'toolkit_print_quota'
          data.blocking_count = this.printCount
          data.blocking_quota_value = this.printQuota
          data.content_type = contentInfo.ctype
          data.title = contentInfo.ct
          break
        default:
          return
      }

      if (this.contentAccessStatus.reportError) {
        data.content_access_report_error = this.contentAccessStatus.reportError
      }

      const userStore = useUserStore()
      const analytics = getAnalyticsTracker()
      analytics.sendEvent('Content Access Blocked', {
        ...data,
        ...contentInfo,
        ...userStore.eventMetadata
      })
    },
    async getUserDownloads() {
      const userStore = useUserStore()
      if (userStore.isAuthenticated) {
        await getUserDownloads().then((result) => {
          if (result.collection && result.collection.content) {
            this.setDownloadedUuids(result.collection.content.map((item: { content_uuid: string }) => item.content_uuid))
          }
        })
      }
    },
    refetchContentAccessReport() {
      const timeout = this.refetchTimeout
      if (timeout) {
        clearTimeout(timeout)
      }
      const userStore = useUserStore()
      this.refetchTimeout = setTimeout(() => {
        getContentCountsForUser(userStore.userId).then(
          (data) => {
            if (data) {
              const updatedReport = { ...this.content_access_report }
              updatedReport.toolkit_copy_count =
                data.toolkit_copy_count > 0
                  ? data.toolkit_copy_count
                  : updatedReport.toolkit_copy_count
              updatedReport.toolkit_print_count =
                data.toolkit_print_count > 0
                  ? data.toolkit_print_count
                  : updatedReport.toolkit_print_count
              updatedReport.toolkit_resource_download_count =
                data.toolkit_resource_download_count > 0
                  ? data.toolkit_resource_download_count
                  : updatedReport.toolkit_resource_download_count
              updatedReport.content_access_report_error = null
              this.setContentAccessReport(updatedReport)
            }
          },
          (error) => {
            const updatedReport = { ...this.content_access_report }
            updatedReport.error = error.message
            this.setContentAccessReport(updatedReport)
          }
        )
      }, 1000)
    }
  },
  getters: {
    copyQuotaExceeded(state): boolean {
      return (
        state.content_access_report.toolkit_copy_count >=
        state.content_access_report.toolkit_copy_quota
      )
    },
    downloadQuotaExceeded(state): boolean {
      return (
        state.content_access_report.toolkit_resource_download_count >=
        state.content_access_report.toolkit_resource_download_quota
      )
    },
    printQuotaExceeded(state): boolean {
      return (
        state.content_access_report.toolkit_print_count >=
        state.content_access_report.toolkit_print_quota
      )
    },
    copyQuota(state): number {
      return state.content_access_report.toolkit_copy_quota
    },
    copyCount(state): number {
      return state.content_access_report.toolkit_copy_count
    },
    printQuota(state): number {
      return state.content_access_report.toolkit_print_quota
    },
    printCount(state): number {
      return state.content_access_report.toolkit_print_count
    },
    downloadQuota(state): number {
      return state.content_access_report.toolkit_resource_download_quota
    },
    downloadCount(state): number {
      return state.content_access_report.toolkit_resource_download_count
    },
    redownloadMap(state): { [key: string]: boolean } {
      const map = {} as {
        [key: string]: boolean
      }
      state.downloaded_uuids.forEach((uuid) => {
        map[uuid] = true
      })
      return map
    },
    contentAccessStatus(state): ContentAccessStatus {
      return {
        copyQuotaExceeded: this.copyQuotaExceeded,
        downloadQuotaExceeded: this.downloadQuotaExceeded,
        printQuotaExceeded: this.printQuotaExceeded,
        redownloadMap: this.redownloadMap,
        report: state.content_access_report,
        reportError: state.content_access_report_error
      }
    },
    maxFoldersReached(state): boolean {
      const libraryStore = useLibraryStore()
      return (
        libraryStore.folderCount >= state.libraryQuotas.maxFolders &&
        state.libraryQuotas.shouldEnforce
      )
    },
    maxContentReachedForFolder(state): (contentType: string, folderUuid: string) => boolean {
      const libraryStore = useLibraryStore()

      return (contentType, folderUuid) => {
        if (!state.libraryQuotas.shouldEnforce) {
          return false
        }
        const savedContentType = savedContentTypeMapping[contentType] || contentType
        const folder = libraryStore.folders.filter((s) => s.uuid === folderUuid)[0]
        if (!folder) {
          throw `Folder ${folderUuid} was not found in user's folders`
        }
        let count =
          folder.content_counts && folder.content_counts[savedContentType]
            ? folder.content_counts[savedContentType]
            : 0
        if (
          [CONTENT_TYPES.SAVED_GOAL, CONTENT_TYPES.SAVED_SUPER_PAGE].indexOf(savedContentType) > -1
        ) {
          count =
            (folder.content_counts ? folder.content_counts[CONTENT_TYPES.SAVED_GOAL] : 0) +
            (folder.content_counts ? folder.content_counts[CONTENT_TYPES.SAVED_SUPER_PAGE] : 0)
        }
        const quota = this.libraryQuotaForContentType(savedContentType)
        return count >= quota
      }
    },
    libraryQuotaForContentType(state): (contentType: string) => number {
      return (contentType: string) => {
        switch (contentType) {
          case CONTENT_TYPES.SAVED_GOAL:
          case CONTENT_TYPES.SAVED_SUPER_PAGE:
            return state.libraryQuotas.maxGoalsPerFolder
          case CONTENT_TYPES.SAVED_STRATEGY:
            return state.libraryQuotas.maxStrategiesPerFolder
          case CONTENT_TYPES.PRESENTLEVEL:
            return state.libraryQuotas.maxPresentlevelsPerFolder
          case CONTENT_TYPES.RESOURCE:
            return state.libraryQuotas.maxResourcesPerFolder
          default:
            return 0
        }
      }
    }
  }
})
